'use strict';

/**
 * Custom Select Dropdown
 * Transforms native select elements into custom expandable dropdowns
 */

class CustomSelect {
    constructor(select) {
        this.select = select;
        
        if (!this.select || this.select.dataset.customSelectInit) return;
        
        this.select.dataset.customSelectInit = 'true';
        this.isOpen = false;
        this.searchBuffer = '';
        this.searchTimeout = null;
        this.init();
    }

    init() {
        this.createWrapper();
        this.createCustomDropdown();
        this.bindEvents();
        this.updateDisplay();
    }

    createWrapper() {
        // Create wrapper and insert it around the select
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'custom-select-wrapper';
        
        // Insert wrapper before select, then move select inside
        this.select.parentNode.insertBefore(this.wrapper, this.select);
        this.wrapper.appendChild(this.select);
    }

    createCustomDropdown() {
        // Create custom dropdown structure
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'custom-select-dropdown';

        // Create display element (shows selected value)
        this.display = document.createElement('div');
        this.display.className = 'custom-select-display';
        this.display.setAttribute('tabindex', '0');
        this.display.setAttribute('role', 'combobox');
        this.display.setAttribute('aria-expanded', 'false');
        
        // Create text span
        this.textSpan = document.createElement('span');
        this.textSpan.className = 'custom-select-text';
        
        // Create arrow element
        this.arrow = document.createElement('span');
        this.arrow.className = 'custom-select-icon';
        
        // Create options list
        this.optionsList = document.createElement('ul');
        this.optionsList.className = 'custom-select-options';
        this.optionsList.setAttribute('role', 'listbox');

        // Populate options
        this.populateOptions();

        // Assemble dropdown
        this.display.appendChild(this.textSpan);
        this.display.appendChild(this.arrow);
        this.dropdown.appendChild(this.display);
        this.dropdown.appendChild(this.optionsList);

        // Hide native select visually but keep it for form submission
        this.select.classList.add('custom-select-hidden');

        // Append custom dropdown to wrapper
        this.wrapper.appendChild(this.dropdown);
    }

    populateOptions() {
        this.optionsList.innerHTML = '';
        
        Array.from(this.select.options).forEach((option, index) => {
            const li = document.createElement('li');
            li.className = 'custom-select-option';
            li.textContent = option.textContent;
            li.dataset.value = option.value;
            li.dataset.index = index;
            li.setAttribute('role', 'option');

            if (option.selected) {
                li.classList.add('selected');
                li.setAttribute('aria-selected', 'true');
            }

            if (option.disabled) {
                li.classList.add('disabled');
            }

            this.optionsList.appendChild(li);
        });
    }

    bindEvents() {
        // Toggle dropdown on display click
        this.display.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });

        // Handle option selection
        this.optionsList.addEventListener('click', (e) => {
            const option = e.target.closest('.custom-select-option');
            if (option && !option.classList.contains('disabled')) {
                this.selectOption(option);
            }
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (!this.wrapper.contains(e.target)) {
                this.close();
            }
        });

        // Keyboard navigation
        this.display.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggle();
            } else if (e.key === 'Escape') {
                this.close();
                this.clearSearch();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!this.isOpen) {
                    this.open();
                } else {
                    this.focusNextOption();
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (this.isOpen) {
                    this.focusPrevOption();
                }
            } else if (e.key === 'Backspace') {
                // Remove last character from search buffer
                this.searchBuffer = this.searchBuffer.slice(0, -1);
                this.resetSearchTimeout();
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                // Type-ahead search: single printable character
                e.preventDefault();
                this.handleTypeAhead(e.key);
            }
        });

        // Sync if native select changes externally
        this.select.addEventListener('change', () => {
            this.updateDisplay();
            this.updateSelectedOption();
        });
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.isOpen = true;
        this.dropdown.classList.add('open');
        this.display.setAttribute('aria-expanded', 'true');
    }

    close() {
        this.isOpen = false;
        this.dropdown.classList.remove('open');
        this.display.setAttribute('aria-expanded', 'false');
    }

    selectOption(optionElement) {
        const index = parseInt(optionElement.dataset.index, 10);
        
        // Update native select
        this.select.selectedIndex = index;
        
        // Trigger change event on native select
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Update display and styling
        this.updateDisplay();
        this.updateSelectedOption();
        
        this.close();
    }

    updateDisplay() {
        const selectedOption = this.select.options[this.select.selectedIndex];
        
        if (selectedOption) {
            this.textSpan.textContent = selectedOption.textContent;
        }
    }

    updateSelectedOption() {
        const options = this.optionsList.querySelectorAll('.custom-select-option');
        options.forEach((option, index) => {
            const isSelected = index === this.select.selectedIndex;
            option.classList.toggle('selected', isSelected);
            option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
    }

    focusNextOption() {
        const options = this.optionsList.querySelectorAll('.custom-select-option:not(.disabled)');
        const currentIndex = this.select.selectedIndex;
        const nextIndex = Math.min(currentIndex + 1, options.length - 1);
        if (options[nextIndex]) {
            this.selectOption(options[nextIndex]);
        }
    }

    focusPrevOption() {
        const options = this.optionsList.querySelectorAll('.custom-select-option:not(.disabled)');
        const currentIndex = this.select.selectedIndex;
        const prevIndex = Math.max(currentIndex - 1, 0);
        if (options[prevIndex]) {
            this.selectOption(options[prevIndex]);
        }
    }

    handleTypeAhead(char) {
        // Open dropdown if not already open
        if (!this.isOpen) {
            this.open();
        }

        // Add character to search buffer
        this.searchBuffer += char.toLowerCase();
        this.resetSearchTimeout();

        // Find matching option
        const options = this.optionsList.querySelectorAll('.custom-select-option:not(.disabled)');
        let matchFound = false;

        for (const option of options) {
            const text = option.textContent.toLowerCase();
            if (text.startsWith(this.searchBuffer)) {
                this.highlightOption(option);
                matchFound = true;
                break;
            }
        }

        // If no match starting with buffer, try finding option that contains the buffer
        if (!matchFound) {
            for (const option of options) {
                const text = option.textContent.toLowerCase();
                if (text.includes(this.searchBuffer)) {
                    this.highlightOption(option);
                    break;
                }
            }
        }
    }

    highlightOption(optionElement) {
        // Remove highlight from all options
        const options = this.optionsList.querySelectorAll('.custom-select-option');
        options.forEach(opt => opt.classList.remove('highlighted'));

        // Add highlight to matching option
        optionElement.classList.add('highlighted');

        // Scroll option into view
        optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

        // Select the option
        this.selectOption(optionElement);
    }

    resetSearchTimeout() {
        // Clear existing timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        // Clear search buffer after 800ms of inactivity
        this.searchTimeout = setTimeout(() => {
            this.clearSearch();
        }, 800);
    }

    clearSearch() {
        this.searchBuffer = '';
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
    }
}

// Initialize all custom selects within Gravity Forms
function initCustomSelects() {
    const selects = document.querySelectorAll('.gform_wrapper select:not([multiple])');
    selects.forEach(select => {
        new CustomSelect(select);
    });
}

// Init on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomSelects);
} else {
    initCustomSelects();
}

// Re-init after Gravity Forms AJAX (for multi-page forms)
if (typeof jQuery !== 'undefined') {
    jQuery(document).on('gform_post_render', initCustomSelects);
}
